/**
 * Outreach Updates (Phase 5c — ongoing respondent communication).
 * Org-authored posts distributed to an outreach's respondents by email
 * (with unsubscribe), in-app notification, and/or the public report timeline.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';

export interface OutreachUpdate {
  id: string;
  type: 'announcement' | 'recipient_response';
  title: string | null;
  body: string;
  recipient_response_id: string | null;
  is_public: boolean;
  created_by: string;
  created_by_name?: string | null;
  sent_at: string | null;
  created_at: string;
  email_delivery_count?: string | number;
  in_app_delivery_count?: string | number;
  /** D7: report pages this update posts to. null = every page of the item. */
  view_ids?: string[] | null;
  target_view_names?: string[] | null;
}

export interface UpdateAudience {
  email_count: number;
  in_app_count: number;
}

export interface CreateUpdatePayload {
  title?: string;
  body: string;
  is_public: boolean;
  channels: { email?: boolean; in_app?: boolean };
  /** D7: omit for all report pages; an array narrows to those reports. */
  report_view_ids?: string[];
}

export const outreachUpdatesQueryKey = (itemId: string) => ['outreach-updates', itemId];

export function useOutreachUpdates(itemId: string) {
  const { data, isLoading } = useQuery(
    outreachUpdatesQueryKey(itemId),
    async () => {
      const response = await api.get(`/outreach-unified/items/${itemId}/updates`);
      return response.data.data as OutreachUpdate[];
    },
    { enabled: !!itemId, staleTime: 30 * 1000 }
  );
  return { updates: data || [], isLoading };
}

export function useUpdateAudience(itemId: string) {
  const { data } = useQuery(
    ['outreach-update-audience', itemId],
    async () => {
      const response = await api.get(`/outreach-unified/items/${itemId}/updates/audience`);
      return response.data.data as UpdateAudience;
    },
    { enabled: !!itemId, staleTime: 60 * 1000 }
  );
  return { audience: data };
}

export function useCreateOutreachUpdate(itemId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    async (payload: CreateUpdatePayload) => {
      const response = await api.post(`/outreach-unified/items/${itemId}/updates`, payload);
      return response.data.data as OutreachUpdate;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(outreachUpdatesQueryKey(itemId));
        toast.success('Update posted — deliveries are sending in the background');
      },
      onError: () => {
        toast.error('Failed to post the update');
      }
    }
  );
}
